import React, { FC, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import Modal from 'react-modal';

import { RootState } from './reducers';
import Start from './containers/Start';
import AgencyStart from './containers/AgencyStart';
import AgencyCheckout from './containers/AgencyCheckout';
import Journey2 from './containers/Journey2';
import URLParameter from './containers/URLParameter';
import Journey1 from './containers/Journey1';
import CreationOrder from './containers/Sujet/CreationOrder/CreationOrder';
import './App.scss';
import bsStyles from '../src/styles/bs.module.scss';
import Dashboard from './containers/Sujet/Dashboard/Dashboard';
import CreationOrderConfirmation from './containers/Sujet/CreationOrder/CreationOrderConfirmation';
import ProductionOrderConfirmation from './containers/Sujet/ProductionOrder/ProductionOrderConfirmation';
import Railposter from './containers/Sujet/Railposter/Railposter';
import ProductionOrder from './containers/Sujet/ProductionOrder/ProductionOrder';
import ErrorModal from './components/Modal/Error/ErrorModal';
import Page404 from './containers/Page404';
import { setType, TypeContainer, Types } from './actions/typeContainer';
import { getDataFromSSObject } from './utils/react';
import { getPathname } from './utils/paths';
import { Redirect } from './components/Redirect';
import { bindActionCreators, Dispatch } from 'redux';
import * as _actionsUI from './actions/ui';
import OrderCompletedOld from './containers/OrderCompletedOld';
import classNames from 'classnames';
import { getParameterByName } from './utils/toSearchParams';
import { useNavigate } from 'react-router';
import { LoginRequired } from './components/ProtectedComponent';
import CampaignDetail from './containers/Sujet/Campaign/CampaignDetail';
import { PaymentRoute } from './components/PaymentRoute';
import DashboardV2 from './containers/Sujet/DashboardV2';
import Header from './components/Header';

import './middlewares/api';

export interface AppProps {
  keycloak: any;
  keycloakReducer: any;
  logOut: any;
  intl: any;
  loading: any;
  location: any;
  containers: TypeContainer;
  actionsUI: typeof _actionsUI;
}

const App: FC<AppProps> = (props) => {
  const dispatch = useDispatch();
  const { intl, keycloakReducer, containers, loading, location } = props;
  const keycloakReady = keycloakReducer.ready;

  const navigate = useNavigate();

  useEffect(() => {
    props.actionsUI.setUiData({ isMobile: window.innerWidth < 767 });

    const handleResize = () => {
      if (window.innerWidth < 767) {
        props.actionsUI.setUiData({ isMobile: true });
      } else {
        props.actionsUI.setUiData({ isMobile: false });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const checkTypeFromHistory = (type: keyof typeof Types): boolean => {
    const ecState = getDataFromSSObject('ecState');
    if (!ecState) {
      return;
    }

    const { urlHistory } = ecState;
    const urlLength = urlHistory.length;
    return (
      urlHistory[urlLength - 1].includes(type) ||
      urlHistory[urlLength - 2]?.includes(type) ||
      urlHistory[urlLength - 3]?.includes(type)
    );
  };

  const checkoutCancelPath =
    checkTypeFromHistory(Types.journey2) || checkTypeFromHistory(Types.journey3)
      ? getPathname(containers)(5)
      : '/journey1/8';

  const checkoutSuccessPath =
    checkTypeFromHistory(Types.journey2) || checkTypeFromHistory(Types.journey3)
      ? getPathname(containers)(6)
      : '/journey1/9';

  const isAgencyCheckoutPath =
    (window as any).location.pathname === '/checkout' ||
    getParameterByName('basketId', (window as any).location.search);

  const setJourneyType = () => {
    const checkType = (type: keyof typeof Types): boolean => props.location.pathname.includes(type);

    const isJourney2 = checkTypeFromHistory(Types.journey2);

    if (isAgencyCheckoutPath) {
      dispatch(setType(Types.journey2));
    } else if (isJourney2 && !checkType(Types.journey3)) {
      dispatch(setType(Types.journey2));
    } else if (checkTypeFromHistory(Types.journey3)) {
      dispatch(setType(Types.journey3));
    } else {
      if (checkType(Types.journey1)) {
        dispatch(setType(Types.journey1));
      } else if (checkType(Types.journey2)) {
        dispatch(setType(Types.journey2));
      } else if (checkType(Types.journey3)) {
        dispatch(setType(Types.journey3));
      } else {
        dispatch(setType(Types.journey1));
      }
    }

    return isJourney2;
  };

  useEffect(() => {
    setJourneyType();
  }, [props.location.pathname]);

  return (
    <>
      <Header />
      <div
        className={classNames(
          {
            'breadcrumbs-top-margin': process.env.REACT_APP_MODE === 'django',
            'standalone-top-margin': process.env.REACT_APP_MODE !== 'django',
          },
          bsStyles.bootstrap,
        )}
      >
        <Routes>
          <Route path='/start' element={<Start intl={intl} />} />

          <Route path='/agency' element={<LoginRequired component={<AgencyStart intl={intl} />} />} />

          <Route path='/' element={<Redirect to='/journey2/1' />} />

          <Route
            path='/checkout/cancel'
            element={
              <LoginRequired
                component={
                  <PaymentRoute redirectPath={checkoutCancelPath} location={location} referrer='/checkout/cancel' />
                }
              />
            }
          />

          <Route
            path='/checkout/exception'
            element={
              <LoginRequired
                component={
                  <PaymentRoute redirectPath={checkoutCancelPath} location={location} referrer='/checkout/exception' />
                }
              />
            }
          />

          <Route
            path='/checkout/decline'
            element={
              <LoginRequired
                component={
                  <PaymentRoute redirectPath={checkoutCancelPath} location={location} referrer='/checkout/decline' />
                }
              />
            }
          />

          <Route
            path='/checkout/accept'
            element={
              <LoginRequired
                component={
                  <PaymentRoute redirectPath={checkoutSuccessPath} location={location} referrer='/checkout/accept' />
                }
              />
            }
          />

          <Route
            path='/checkout-agency'
            element={<LoginRequired component={<AgencyCheckout keycloakReady={keycloakReady} intl={intl} />} />}
          />

          <Route path='/journey1/' element={<Redirect to='/journey1/1' />} />

          <Route path='/journey1/:step' element={<Journey1 keycloakReady={keycloakReady} intl={intl} />} />

          <Route path='/journey2/' element={<Redirect to='/journey2/1' />} />

          <Route path='/journey2/:step' element={<Journey2 keycloakReady={keycloakReady} intl={intl} />} />

          <Route path='/productlist/' element={<URLParameter keycloakReady={keycloakReady} intl={intl} />} />

          <Route path='/journey3/' element={<Redirect to={'/journey3/campaigns'} />} />

          <Route
            path='/journey3/campaigns'
            element={<LoginRequired component={<Dashboard keycloakReady={keycloakReady} intl={intl} journey3 />} />}
          />

          {/* Journey3 loads user-owned baskets by id without basketreference, which the backend rejects (412) when not logged in */}
          <Route
            path='/journey3/:step/:campaignId'
            element={<LoginRequired component={<Journey2 keycloakReady={keycloakReady} intl={intl} />} />}
          />

          <Route
            path='/journey3/:step'
            element={<LoginRequired component={<Journey2 keycloakReady={keycloakReady} intl={intl} />} />}
          />

          {/* After sales management */}
          <Route
            path='/dashboardold'
            element={<LoginRequired component={<Dashboard keycloakReady={keycloakReady} intl={intl} />} />}
          />

          <Route
            path='/dashboard'
            element={<LoginRequired component={<DashboardV2 keycloakReady={keycloakReady} intl={intl} />} />}
          />

          <Route
            path='/campaign/:campaignId'
            element={<LoginRequired component={<CampaignDetail keycloakReady={keycloakReady} intl={intl} />} />}
          />

          <Route
            path='/offerte/:campaignId'
            element={
              <LoginRequired
                component={<CampaignDetail {...props} keycloakReady={keycloakReady} intl={intl} isOfferte />}
              />
            }
          />

          <Route
            path='/offerte/:campaignId/completed'
            element={
              <LoginRequired
                component={
                  <OrderCompletedOld
                    journey2
                    {...props}
                    keycloakReady={keycloakReady}
                    intl={intl}
                    isOfferte
                    loading={loading['general']}
                  />
                }
              />
            }
          />

          <Route
            path='/campaign/:campaignId/production-order-new'
            element={<LoginRequired component={<ProductionOrder keycloakReady={keycloakReady} intl={intl} />} />}
          />

          <Route
            path='/campaign/:campaignId/production-order/:productionId'
            element={
              <LoginRequired component={<ProductionOrderConfirmation keycloakReady={keycloakReady} intl={intl} />} />
            }
          />

          <Route path='/campaign/:campaignId/production-order-railposters' element={<Railposter />} />

          <Route
            path='/campaign/:campaignId/creation-order-new'
            element={<LoginRequired component={<CreationOrder keycloakReady={keycloakReady} intl={intl} />} />}
          />

          <Route
            path='/campaign/:campaignId/creation-order-new-digital'
            element={
              <LoginRequired component={<CreationOrder isDigital keycloakReady={keycloakReady} intl={intl} />} />
            }
          />

          <Route
            path='/campaign/:campaignId/creation-order/:creationId'
            element={
              <LoginRequired component={<CreationOrderConfirmation keycloakReady={keycloakReady} intl={intl} />} />
            }
          />

          <Route
            path='/campaign/:campaignId/creation-order-digital/:creationId'
            element={
              <LoginRequired
                component={<CreationOrderConfirmation isDigital keycloakReady={keycloakReady} intl={intl} />}
              />
            }
          />

          <Route path='*' element={<Page404 />} />
        </Routes>

        {props.location.search.includes('error=true') && (
          <Modal
            key='uniqueModal'
            isOpen={props.location.search.includes('error=true')}
            shouldCloseOnOverlayClick={true}
            className={'large-modal'}
            closeTimeoutMS={0}
            overlayClassName={'overlay'}
          >
            <div className={classNames('modal background-light-grey errorModal', bsStyles.bootstrap)}>
              <ErrorModal
                {...props}
                intl={intl}
                location={props.location}
                onClose={() => {
                  navigate({
                    search: props.location.search.split('&')[0].split('error=true')[0],
                  });
                }}
              />
            </div>
          </Modal>
        )}
      </div>
    </>
  );
};

function mapStateToProps(state: RootState, ownProps: any) {
  return {
    ...ownProps,
    ...state.app,
    ...state.router,
    location: state.router.location,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actionsUI: bindActionCreators(_actionsUI as any, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(App as any));
